import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Plus, Save, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLANK = {
  slug: "",
  n: "",
  title: "",
  lead: "",
  deliverables: [],
  cta: "",
  order: 0,
  published: true,
};

export default function AdminServices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/services`, { withCredentials: true });
      setItems(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    try {
      if (payload.id) {
        const { id, ...body } = payload;
        await axios.patch(`${API}/admin/services/${id}`, body, { withCredentials: true });
        toast.success("Updated");
      } else {
        await axios.post(`${API}/admin/services`, payload, { withCredentials: true });
        toast.success("Created");
      }
      setEditing(null);
      setCreating(false);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(x => x.msg).join(", ") : d || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      await axios.delete(`${API}/admin/services/${id}`, { withCredentials: true });
      setItems((prev) => prev.filter((c) => c.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-services">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <p className="eyebrow">Content</p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl leading-tight tracking-tight">Services</h1>
        </div>
        <button
          onClick={() => {
            const nextN = String(items.length + 1).padStart(2, "0");
            setCreating(true);
            setEditing({ ...BLANK, n: nextN, order: items.length + 1 });
          }}
          data-testid="svc-add"
          className="btn-primary"
        >
          <Plus size={14} /> New service
        </button>
      </div>

      {loading ? (
        <div className="mt-10 text-[#A9A9A6]">Loading…</div>
      ) : (
        <div className="mt-8 border border-[#343437]">
          {items.length === 0 && <div className="p-8 text-[#A9A9A6] text-sm">No services yet.</div>}
          {items.map((s) => (
            <div
              key={s.id}
              className="border-t border-[#343437] first:border-t-0 p-5 lg:p-6 grid grid-cols-1 lg:grid-cols-[80px_1fr_140px_200px] gap-4 items-center"
              data-testid={`svc-row-${s.slug}`}
            >
              <span className="oversize text-2xl text-[#D8A936]">{s.n}</span>
              <div>
                <p className="font-display text-[17px] text-[#FAF9F6]">{s.title}</p>
                <p className="text-xs text-[#A9A9A6] mt-1 line-clamp-2">{s.lead}</p>
              </div>
              <span className={`text-[10px] tracking-[0.22em] uppercase ${s.published ? "text-[#D8A936]" : "text-[#A9A9A6]"}`}>
                {s.published ? "Published" : "Unpublished"}
              </span>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setEditing(s)}
                  data-testid={`svc-edit-${s.slug}`}
                  className="px-3 py-1.5 border border-[#343437] text-xs tracking-widest uppercase hover:border-[#D8A936] hover:text-[#D8A936] transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => remove(s.id)}
                  data-testid={`svc-delete-${s.slug}`}
                  className="p-1.5 border border-[#343437] hover:border-red-400 hover:text-red-400 transition-colors"
                  aria-label="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ServiceEditor
          initial={editing}
          isNew={creating}
          onCancel={() => { setEditing(null); setCreating(false); }}
          onSave={save}
        />
      )}
    </div>
  );
}

function ServiceEditor({ initial, isNew, onCancel, onSave }) {
  const [form, setForm] = useState({
    ...initial,
    deliverables: Array.isArray(initial.deliverables) ? initial.deliverables.join("\n") : "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      deliverables: form.deliverables.split("\n").map((t) => t.trim()).filter(Boolean),
      order: Number(form.order) || 0,
    });
  };

  return (
    <div className="fixed inset-0 z-[80] bg-[#0B0B0C]/85 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-auto">
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#151517] border border-[#343437] w-full max-w-2xl p-8 space-y-5 relative my-8"
        data-testid="svc-editor"
      >
        <button type="button" onClick={onCancel} className="absolute top-4 right-4 text-[#A9A9A6] hover:text-[#FAF9F6]" aria-label="Close">
          <X size={18} />
        </button>
        <p className="eyebrow">{isNew ? "New" : "Edit"}</p>
        <h2 className="font-display text-2xl">Service</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <TextField label="Slug" value={form.slug} onChange={(v) => set("slug", v)} required testid="svc-slug" />
          <TextField label="Number label (e.g. 01)" value={form.n} onChange={(v) => set("n", v)} required testid="svc-n" />
          <TextField label="Order" type="number" value={form.order} onChange={(v) => set("order", v)} testid="svc-order" />
        </div>
        <TextField label="Title" value={form.title} onChange={(v) => set("title", v)} required testid="svc-title" />
        <TextArea label="Lead paragraph" value={form.lead} onChange={(v) => set("lead", v)} required testid="svc-lead" />
        <TextArea label="Deliverables (one per line)" value={form.deliverables} onChange={(v) => set("deliverables", v)} testid="svc-deliverables" rows={6} />
        <TextField label="Call to action label" value={form.cta} onChange={(v) => set("cta", v)} required testid="svc-cta" />
        <label className="flex items-center gap-3 text-sm text-[#FAF9F6]">
          <input
            type="checkbox"
            checked={form.published}
            onChange={(e) => set("published", e.target.checked)}
            className="accent-[#D8A936]"
            data-testid="svc-published"
          />
          Published (visible on public site)
        </label>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onCancel} className="btn-ghost">Cancel</button>
          <button type="submit" data-testid="svc-save" className="btn-primary">
            <Save size={14} /> {isNew ? "Create" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

function TextField({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}{required && <span className="text-[#D8A936]"> *</span>}</span>
      <input
        type={type}
        required={required}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-3 py-2.5 outline-none focus:border-[#D8A936]"
      />
    </label>
  );
}

function TextArea({ label, value, onChange, required, testid, rows = 4 }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}{required && <span className="text-[#D8A936]"> *</span>}</span>
      <textarea
        rows={rows}
        required={required}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-3 py-2.5 outline-none focus:border-[#D8A936] resize-none"
      />
    </label>
  );
}
